import { type LmsTypeCapitalized } from '../components/classroom/types';
import { type ClassLevelOptions } from '../types/adminConfig';
import type { LmsSyncSettings } from '../types/apiTypes';

export enum ClassStatus {
    Published = 'published',
    Draft = 'draft'
}

export interface ClassSession {
    session_id: string;
    class_id: string;
    timezone: string;
    start_time: string;
    end_time: string;
    last_updated: string;
    attendance_generated: string | null;
    disable_attendance: boolean;
    attendance_start_time: string;
    topic: string | null;
}

export interface ClassSessionGroupSchedule {
    class_times: string[];
}

export interface ClassSessionGroupConfig {
    label: string;
    selectedGroups: (number | string)[];
    trackAttendance: boolean;
    attendanceLmsPush: boolean;
}

export type ClassSessionGroupMembership = {
    class_id: number;
    class_session_group_uuid: string;
    class_session_uuid: string;
    class_uuid: string;
};

export interface ClassSessionGroup {
    class_session_group_uuid: string;
    class_id: number;
    class_uuid: string;
    schedule: ClassSessionGroupSchedule;
    config: ClassSessionGroupConfig;
    memberships: ClassSessionGroupMembership[];
}

/**
 * ClassSessionInfo is returned from specific endpoints, such as the get_class_full_info.
 */
export interface ClassSessionInfo {
    sid: number;
    class_id: string;
    start_time: string;
    attendance_start_time: string | null; // ISO String from JSON
    end_time: string;
    weekday_str: string;
    date_str: string;
    start_time_str: string;
    end_time_str: string;
    status: string;
    type: string | null;
    timezone: string;
    class_session_uuid: string;
    class_session_group_uuid?: string;
    state_data: any;
    last_updated: string;
    attendance_generated: string | null;
    class_session_topic: string | null;
    metrics_generated: string | null;
    class_uuid: string;
    disable_attendance: boolean;
    class_session_group?: ClassSessionGroup;
}

export interface ClassStudentGroup {
    c_student_group_id: number;
    class_id: string;
    label: string;
    data?: any;
    lms_section_id?: string;
    lms_group_id?: string;
    lms_group_set_id?: string;
    is_section: boolean;
}

export interface ClassStudentGroupSet {
    class_id: number;
    lms_id: string;
    name: string;
}

export interface GroupSectionLabel {
    label: string;
    section?: string;
}

export enum Roles {
    Teacher = 'teacher',
    Assistant = 'assistant',
    Student = 'student'
}

export interface ClassStudent {
    student_id: string;
    meeting_id: string;
    first_name: string;
    last_name: string;
    user_name: string;
    user_image: string;
    role: Roles;
    student_uuid: string;
    seed: number;
    email: string;
    ext_user_id: string | null;
}

type ClassType = 'class' | 'template';

export type ClassRecordData = {
    class_code: string;
    class_name: string;
    class_description: string | null;
    class_times?: string;
    school_name?: string;
    timezone: string;
    locked?: boolean;
    content_type?: string;
    template_id?: string;
};
// Renamed this to ClassRecordFromApi because it is not the same as ClassRecord in /node_pod/models/types.ts and the code got confusing.
export interface ClassRecordFromApi {
    // This is probably better described as Partial<ClassForExport> but we can't link it because this types.ts is used in /node_pod/models/types.ts
    sid: number;
    parent?: number;
    class_code?: string; // not optional in ClassRecord
    class_description?: string; // not optional in classRecord.
    class_group_ids?: number[];
    class_name: string;
    class_options?: ClassLevelOptions; // not optional in ClassRecord
    class_uuid?: string; // not optional in ClassRecord
    content_type?: string;
    data?: ClassRecordData; // not optional in ClassRecord
    first_name?: string; // not in ClassRecord
    isMeetingOwner?: boolean; // not part of ClassRecord but part of ClassRecordForExport
    last_name?: string; // not in ClassRecord
    lms_sync_settings?: LmsSyncSettings; // not in ClassRecord
    lms_type?: LmsTypeCapitalized; // not in ClassRecord
    lti_state?: string; // any | null in ClassRecord
    meeting_id: string;
    meeting_provider?: string; // not in ClassRecord
    password?: string; // not in ClassRecord
    school_name: string; // not optional in ClassRecord
    status?: ClassStatus; // in ClassRecord the enum is defined in /node_pod/models/types.ts but same properties.
    type?: ClassType; // propertries differ between local ClassType and modeles/types.ts ClassType missing "office-hours" and "demo" enum values
}

export interface SubClassRecord extends ClassRecordFromApi {
    leadInstructor?: { userName: string };
    classGroups?: ClassStudentGroup[];
    classGroupSets?: ClassStudentGroupSet[];
}

export interface SubClassesResponse {
    mainClass: SubClassRecord;
    subClasses: SubClassRecord[];
}
